import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { collection, addDoc, doc, getDoc, getDocFromServer } from 'firebase/firestore';
import { db, auth, usesLocalFirebaseEmulators } from '@/lib/firebase';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import {
  normalizeSelectedTests,
  getExpertBenchmark,
  ROLE_TYPE_IDS,
  ROLE_TYPE_LABELS,
} from '@/config/tests';
import { getRecommendedTests } from '@/lib/testRecommendation';
import { getAllOfficialTests } from '@/config/batteries';
import { INDUSTRIES } from '@/config/industries';
import { BatterySelectorList } from '@/components/app/BatterySelectorList';
import type { RoleType, TestKey } from '@/config/tests';
import type { IndustryId } from '@/config/industries';

function isPermissionError(err: unknown): boolean {
  if (err && typeof err === 'object') {
    const code = (err as { code?: string }).code;
    const message = (err as { message?: string }).message ?? '';
    if (code === 'permission-denied') return true;
    if (/permission|insufficient/i.test(message)) return true;
  }
  return false;
}

const ALLOWED_CREATE_ROLES = ['admin', 'recruiter'] as const;

function canRoleCreateProcess(role: string): boolean {
  return ALLOWED_CREATE_ROLES.includes(role as (typeof ALLOWED_CREATE_ROLES)[number]);
}

function companyIdAsString(companyId: string | null | undefined): string | null {
  if (companyId == null) return null;
  if (typeof companyId === 'string') return companyId.trim() || null;
  const ref = companyId as { id?: string };
  if (ref && typeof ref.id === 'string') return ref.id;
  return null;
}

export function NewProcessPage() {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [roleType, setRoleType] = useState<RoleType | ''>('');
  const [selectedTestKeys, setSelectedTestKeys] = useState<TestKey[]>([]);
  const [userModifiedBattery, setUserModifiedBattery] = useState(false);
  const [companyIndustry, setCompanyIndustry] = useState<IndustryId | null>(null);
  const [presetIndustry, setPresetIndustry] = useState<IndustryId | null>(null);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [fixingUser, setFixingUser] = useState(false);
  const [success, setSuccess] = useState(false);
  const [createdProcessId, setCreatedProcessId] = useState<string | null>(null);
  const { appUser, loading: authLoading, refreshAppUser } = useAuth();
  const navigate = useNavigate();

  const currentUser = auth.currentUser;
  const uid = currentUser?.uid ?? null;
  const companyIdStr = appUser ? companyIdAsString(appUser.companyId) : null;

  // Cargar industry de la empresa (fetch puntual)
  useEffect(() => {
    if (!companyIdStr) return;
    let cancelled = false;
    getDoc(doc(db, 'companies', companyIdStr)).then((snap) => {
      if (cancelled) return;
      if (snap.exists()) {
        const ind = (snap.data().industry as IndustryId) ?? null;
        setCompanyIndustry(ind);
        setPresetIndustry((prev) => (prev == null ? ind : prev));
      }
    });
    return () => {
      cancelled = true;
    };
  }, [companyIdStr]);

  const industryForPreset = presetIndustry ?? companyIndustry;

  const recommendedTests = industryForPreset && roleType
    ? getRecommendedTests(industryForPreset, roleType)
    : [];

  const selectionMatchesRecommendation =
    recommendedTests.length > 0 &&
    selectedTestKeys.length === recommendedTests.length &&
    recommendedTests.every((t) => selectedTestKeys.includes(t));

  const canRestoreRecommendation =
    industryForPreset &&
    roleType &&
    recommendedTests.length > 0 &&
    !selectionMatchesRecommendation;

  // Aplicar recomendación cuando cambian industry/role y el usuario no ha modificado la batería
  useEffect(() => {
    if (userModifiedBattery || !industryForPreset || !roleType) return;
    const recommended = getRecommendedTests(industryForPreset, roleType);
    if (recommended.length > 0) setSelectedTestKeys(recommended);
  }, [industryForPreset, roleType, userModifiedBattery]);

  const availableTests = getAllOfficialTests();
  const recommendationApplied = Boolean(
    industryForPreset && roleType && !userModifiedBattery && recommendedTests.length > 0
  );

  const handlePresetIndustryChange = (value: string) => {
    const ind = (value || null) as IndustryId | null;
    setPresetIndustry(ind);
    setUserModifiedBattery(false);
    if (ind && roleType) {
      const recommended = getRecommendedTests(ind, roleType);
      if (recommended.length > 0) setSelectedTestKeys(recommended);
    }
  };

  const handlePresetRoleTypeChange = (value: string) => {
    const rt = (value || '') as RoleType | '';
    setRoleType(rt);
    setUserModifiedBattery(false);
    if (industryForPreset && rt) {
      const recommended = getRecommendedTests(industryForPreset, rt);
      if (recommended.length > 0) setSelectedTestKeys(recommended);
    }
  };

  const handleRestoreRecommendation = () => {
    if (!industryForPreset || !roleType || recommendedTests.length === 0) return;
    setUserModifiedBattery(false);
    setSelectedTestKeys(recommendedTests);
  };

  const toggleTest = (key: TestKey) => {
    setUserModifiedBattery(true);
    setSelectedTestKeys((prev) => {
      const next = prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key];
      return normalizeSelectedTests(next);
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (authLoading) return;
    if (!appUser) {
      setError('Debes iniciar sesión para crear un proceso.');
      return;
    }
    if (!companyIdStr) {
      setError('No tienes una empresa asignada (companyId es obligatorio). Contacta al administrador.');
      return;
    }
    if (!canRoleCreateProcess(appUser.role)) {
      setError('Solo usuarios con rol admin o reclutador pueden crear procesos.');
      return;
    }
    if (!uid || !auth.currentUser) {
      setError('Sesión no disponible. Recarga la página e intenta de nuevo.');
      return;
    }
    if (!roleType) {
      setError('El tipo de puesto es obligatorio.');
      return;
    }

    setSubmitting(true);
    try {
      await auth.currentUser.getIdToken(true);
      const payload = {
        companyId: companyIdStr,
        createdBy: auth.currentUser.uid,
        title,
        description: description || null,
        status: 'draft',
        roleType: roleType as RoleType,
        testKeys: normalizeSelectedTests(selectedTestKeys),
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };
      const ref = await addDoc(collection(db, 'processes'), payload);
      const processRef = doc(db, 'processes', ref.id);
      for (let i = 0; i < 15; i++) {
        const snap = await getDocFromServer(processRef);
        if (snap.exists()) {
          setCreatedProcessId(ref.id);
          setSuccess(true);
          setSubmitting(false);
          return;
        }
        await new Promise((r) => setTimeout(r, 400));
      }
      setCreatedProcessId(ref.id);
      setSuccess(true);
    } catch (err: unknown) {
      const code = err && typeof err === 'object' ? (err as { code?: string }).code : undefined;
      const message = err instanceof Error ? err.message : String(err);
      if (isPermissionError(err)) {
        const hint = companyIdStr
          ? ' Puedes pulsar "Corregir companyId en mi usuario" para guardar companyId como texto.'
          : '';
        const emulatorHint = usesLocalFirebaseEmulators
          ? ' La app en `vite dev` usa emuladores de Auth/Firestore: los datos están en local, no en la consola de Firebase en la nube. Usa `npm run build` + preview o crea el mismo usuario en el emulator.'
          : '';
        setError(
          `Firestore rechazó la operación (${code ?? 'permission-denied'}). ${message}.${hint}${emulatorHint}`
        );
      } else {
        setError(message || 'Error al crear el proceso');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const canCreate =
    !authLoading &&
    appUser != null &&
    companyIdStr != null &&
    canRoleCreateProcess(appUser.role) &&
    uid != null;

  const handleFixUserCompanyId = async () => {
    if (!uid || !companyIdStr) return;
    setFixingUser(true);
    setError('');
    try {
      const { updateDoc } = await import('firebase/firestore');
      await updateDoc(doc(db, 'users', uid), { companyId: companyIdStr });
      await refreshAppUser();
      setError('');
    } finally {
      setFixingUser(false);
    }
  };

  if (success && createdProcessId) {
    return (
      <div className="space-y-6">
        <Card className="max-w-xl">
          <CardHeader>
            <CardTitle>Proceso creado</CardTitle>
            <CardDescription>El proceso se ha guardado con éxito.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Puedes configurar candidatos e invitar a evaluar desde la ficha del proceso.
            </p>
            <Button asChild>
              <Link to={`/app/processes/${createdProcessId}`}>Aceptar</Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Button variant="ghost" size="sm" asChild>
        <Link to="/app/processes">← Procesos</Link>
      </Button>
      <Card className="max-w-xl">
        <CardHeader>
          <CardTitle>Nuevo proceso</CardTitle>
          <CardDescription>Define el título, tipo de puesto y la batería de pruebas del proceso.</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            {error && (
              <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
                <p>{error}</p>
                {error.includes('permission-denied') && uid && companyIdStr && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="mt-2"
                    onClick={handleFixUserCompanyId}
                    disabled={fixingUser}
                  >
                    {fixingUser ? 'Corrigiendo...' : 'Corregir companyId en mi usuario'}
                  </Button>
                )}
              </div>
            )}
            {!authLoading && appUser != null && !canCreate && (
              <div className="rounded-md bg-muted p-3 text-sm text-muted-foreground">
                {!companyIdStr
                  ? 'Tu usuario no tiene empresa asignada (companyId es obligatorio). No puedes crear procesos.'
                  : !canRoleCreateProcess(appUser.role)
                    ? 'Solo usuarios con rol admin o reclutador pueden crear procesos.'
                    : !uid
                      ? 'Sesión no disponible. Recarga la página.'
                      : 'No se puede crear el proceso. Comprueba tu sesión.'}
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="title">Título</Label>
              <Input
                id="title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Ej. Selección Comercial 2025"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Descripción (opcional)</Label>
              <textarea
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Breve descripción del proceso"
                className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              />
            </div>

            <div className="rounded-lg border border-border bg-muted/30 p-4 space-y-4">
              <h3 className="text-sm font-medium">Configuración del Puesto</h3>
              <p className="text-xs text-muted-foreground">
                Selecciona el área y tipo de puesto para aplicar la preselección inteligente de pruebas recomendadas.
              </p>
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="presetIndustry">Área / Industria</Label>
                  <select
                    id="presetIndustry"
                    value={presetIndustry ?? companyIndustry ?? ''}
                    onChange={(e) => handlePresetIndustryChange(e.target.value)}
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  >
                    <option value="">Selecciona el área</option>
                    {INDUSTRIES.map((ind) => (
                      <option key={ind.id} value={ind.id}>
                        {ind.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="presetRoleType">Tipo de Puesto *</Label>
                  <select
                    id="presetRoleType"
                    value={roleType}
                    onChange={(e) => handlePresetRoleTypeChange(e.target.value)}
                    required
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  >
                    <option value="">Selecciona el tipo de puesto</option>
                    {ROLE_TYPE_IDS.map((id) => (
                      <option key={id} value={id}>
                        {ROLE_TYPE_LABELS[id]}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              {recommendationApplied && (
                <p className="text-xs text-green-600 dark:text-green-500">
                  Batería sugerida aplicada
                </p>
              )}
              {canRestoreRecommendation && (
                <button
                  type="button"
                  onClick={handleRestoreRecommendation}
                  className="text-xs text-primary hover:underline"
                >
                  Restablecer recomendación inteligente
                </button>
              )}
              {roleType && getExpertBenchmark(roleType as RoleType) && (
                <div className="mt-4 rounded-lg bg-violet-50 dark:bg-violet-950/30 border border-violet-200 dark:border-violet-800 p-3 text-sm text-violet-800 dark:text-violet-200">
                  🧠 Perfil de Afinidad Inteligente Activado. PsyTalent evaluará a los candidatos usando el benchmark experto para este puesto.
                </div>
              )}
            </div>

            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label>Batería de pruebas</Label>
                {recommendationApplied && (
                  <span className="text-xs text-muted-foreground">
                    Batería sugerida según industria y tipo de puesto.
                  </span>
                )}
              </div>
              <div className="rounded-md border border-input p-3">
                <BatterySelectorList
                  tests={availableTests}
                  selected={selectedTestKeys}
                  onToggle={toggleTest}
                  jkGroupExpandedByDefault={recommendationApplied}
                />
              </div>
            </div>

            <div className="flex gap-2">
              <Button type="submit" disabled={authLoading || submitting || !canCreate}>
                {submitting ? 'Creando...' : 'Crear proceso'}
              </Button>
              <Button type="button" variant="outline" asChild>
                <Link to="/app/processes">Cancelar</Link>
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
